import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BinocularsIcon, SpinnerGapIcon, RocketLaunchIcon, TrashIcon, PlusIcon, RobotIcon, HandPointingIcon, ChartLineUpIcon } from "@phosphor-icons/react";
import { fetchSurvivorWatch, addSurvivorWatch, removeSurvivorWatch } from "../lib/api";
import { QuickBuyLink } from "../components/TradeButton";
import { fmtUsd } from "../lib/format";
import { useSeo } from "../lib/useSeo";

const GOLD = "#F0C000";
const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");
const ago = (ts) => {
  if (!ts) return "";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const WatchRow = ({ w, onRemove }) => {
  const popped = w.status === "popped";
  const accent = popped ? "#FF7A00" : "#8A8F98";
  return (
    <div
      data-testid={`survivor-row-${w.mint}`}
      className="relative flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5 border transition-colors hover:bg-white/[0.03]"
      style={{ borderColor: `${accent}33`, background: popped ? `linear-gradient(90deg, ${accent}16, transparent)` : "transparent" }}
    >
      {w.image ? (
        <img src={w.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
      ) : (
        <div className="w-9 h-9 flex items-center justify-center border shrink-0" style={{ borderColor: `${accent}55`, color: accent }}>
          {popped ? <RocketLaunchIcon size={18} weight="fill" /> : <BinocularsIcon size={18} />}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/coin/${w.mint}`} className="font-heading font-bold hover:text-neon transition-colors">${w.symbol || short(w.mint)}</Link>
          {popped ? (
            <span className="font-mono text-[9px] px-1.5 py-0.5 font-bold animate-pulse" style={{ background: "#FF7A0022", border: "1px solid #FF7A00", color: "#FF7A00" }}>🚀 POPPED</span>
          ) : (
            <span className="font-mono text-[9px] px-1.5 py-0.5 font-bold" style={{ background: "#8A8F9820", border: "1px solid #8A8F9855", color: "#B7BCC4" }}>👀 WATCHING</span>
          )}
          <span className="font-mono text-[9px] px-1.5 py-0.5 inline-flex items-center gap-1 text-muted-foreground border border-white/10">
            {w.source === "auto" ? <><RobotIcon size={10} /> auto</> : <><HandPointingIcon size={10} /> yours</>}
          </span>
        </div>
        <div className="font-mono text-[11px] text-muted-foreground truncate mt-0.5">
          {w.reason} · added {ago(w.addedTs)}{popped ? ` · popped ${ago(w.poppedTs)}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <div className="font-mono text-sm font-bold">{fmtUsd(w.lastMcap)}</div>
        <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">mcap</div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <QuickBuyLink coin={{ address: w.mint }} testid={`survivor-buy-${w.mint}`} />
        <Link to={`/coin/${w.mint}`} title="Open chart & Degen Score" className="p-1.5 text-muted-foreground hover:text-neon transition-colors" data-testid={`survivor-chart-${w.mint}`}>
          <ChartLineUpIcon size={16} weight="bold" />
        </Link>
        <button onClick={() => onRemove(w.mint)} title="Remove from watch" data-testid={`survivor-remove-${w.mint}`} className="p-1.5 text-muted-foreground hover:text-danger transition-colors">
          <TrashIcon size={16} />
        </button>
      </div>
    </div>
  );
};

export default function SurvivorWatch() {
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const [adding, setAdding] = useState(false);

  useSeo({
    title: "🔭 Survivor Watch — Track Quality Coins Before They Pop | AlphaApeing",
    description: "A watchlist of quality survivor coins the site auto-tracks (real handle, liquid, aged) plus your own paste-ins — so a CYBERLEEK-style breakout pings you before you miss it.",
  });

  const { data, isLoading } = useQuery({ queryKey: ["survivorWatch"], queryFn: fetchSurvivorWatch, refetchInterval: 60000 });
  const watch = data?.watch || [];
  const popped = watch.filter((w) => w.status === "popped");
  const watching = watch.filter((w) => w.status !== "popped");

  const submit = async () => {
    if (!input.trim()) return;
    setAdding(true);
    try {
      const res = await addSurvivorWatch(input.trim());
      toast.success(`Added ${res.added} coin${res.added === 1 ? "" : "s"} to your Survivor Watch`);
      setInput("");
      qc.invalidateQueries({ queryKey: ["survivorWatch"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add — paste valid Solana token addresses");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (mint) => {
    await removeSurvivorWatch(mint);
    qc.invalidateQueries({ queryKey: ["survivorWatch"] });
    toast.success("Removed from watch");
  };

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="survivor-watch-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black" style={{ background: GOLD }}><BinocularsIcon size={24} weight="bold" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">SURVIVOR <span style={{ color: GOLD }}>WATCH</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Quality <span className="text-white font-semibold">survivors</span> tracked <span style={{ color: GOLD }}>before</span> they pop. The site auto-adds coins that
          fit the CYBERLEEK profile — aged, liquid, real X handle — and you can paste your own below. The moment any of them triggers a
          <Link to="/candle-radar" className="text-neon hover:underline"> Breakout</Link>, you get pinged (Telegram breakout alert).
        </p>
      </div>

      <div className="border border-white/10 bg-card p-4 space-y-3" data-testid="survivor-add-box">
        <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Paste token addresses (comma, space or new-line separated)</label>
        <textarea
          data-testid="survivor-add-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="ApZuxdpzMrbEYTGEzeY9afh5pj9d6qPRJCTgQYiipbKg, ..."
          rows={3}
          className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono text-white outline-none focus:border-neon/60 resize-none"
        />
        <button
          data-testid="survivor-add-btn"
          onClick={submit}
          disabled={adding || !input.trim()}
          className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-black disabled:opacity-50"
          style={{ background: GOLD }}
        >
          {adding ? <SpinnerGapIcon size={16} className="animate-spin" /> : <PlusIcon size={16} weight="bold" />} Add to Watch
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><SpinnerGapIcon size={30} className="animate-spin" style={{ color: GOLD }} /></div>
      ) : watch.length === 0 ? (
        <div className="border border-white/10 bg-card py-16 text-center text-muted-foreground font-mono text-sm px-6">
          Nothing on watch yet. The site auto-adds survivor candidates as it scans — or paste your own above.
        </div>
      ) : (
        <>
          {popped.length > 0 && (
            <div className="space-y-3" data-testid="survivor-popped-section">
              <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2"><span>🚀</span> Popped <span className="font-mono text-xs text-muted-foreground">{popped.length}</span></h2>
              <div className="border divide-y divide-white/5" style={{ borderColor: "#FF7A0033" }}>
                {popped.map((w) => <WatchRow key={w.mint} w={w} onRemove={remove} />)}
              </div>
            </div>
          )}
          <div className="space-y-3" data-testid="survivor-watching-section">
            <h2 className="font-heading font-black text-xl tracking-tight flex items-center gap-2"><span>👀</span> Watching <span className="font-mono text-xs text-muted-foreground">{watching.length}</span></h2>
            {watching.length === 0 ? (
              <div className="border border-white/10 bg-card py-8 text-center text-muted-foreground font-mono text-xs px-6">All caught up — everything on watch has already popped.</div>
            ) : (
              <div className="border border-white/5 divide-y divide-white/5">
                {watching.map((w) => <WatchRow key={w.mint} w={w} onRemove={remove} />)}
              </div>
            )}
          </div>
        </>
      )}

      <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground border-t border-white/5 pt-4">
        <BinocularsIcon size={13} style={{ color: GOLD }} />
        Auto-adds refresh every 3 min; unpopped auto-entries expire after 7 days. Signals, not certainties — not financial advice.
      </div>
    </div>
  );
}
